import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getRoot } from 'lexical';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { forwardRef, useCallback, useRef, useState } from 'react';
import { twJoin } from 'tailwind-merge';

import { composeRefs } from 'lib/utils';

import ParagraphNode from './Nodes/ParagraphNode';
import RichText from './RichText';

const Toolbar = dynamic(() => import('./Toolbar'), { ssr: false });
const ErrorMessage = dynamic(() => import('./ErrorMessage'), { ssr: false });
const Label = dynamic(() => import('./Label'));

const TextEditor = forwardRef(
  (
    {
      answering,
      className,
      disabled = false,
      error = null,
      gaEventCategory,
      initialValue,
      label,
      placeholder,
      toolbarItems,
      onBlur,
      onCancel,
      onChange,
      onFocus,
      onSend,
    },
    ref
  ) => {
    const editorRef = useRef(null);
    const [editor] = useLexicalComposerContext();
    const [isSendEnabled, setSendEnabled] = useState(false);

    const onChangeHandler = useCallback(
      (editorState) => {
        editor.update(() => {
          const isEditorEmpty = $getRoot()
            .getChildren()
            .every(
              (child) => child instanceof ParagraphNode && child.isEmpty()
            );

          setSendEnabled(!isEditorEmpty);
        });

        if (onChange) {
          onChange(editorState);
        }
      },
      [editor, onChange]
    );

    return (
      <>
        {label && <Label>{label}</Label>}
        <div
          ref={composeRefs(ref, editorRef)}
          className={twJoin('w-full', className)}
        >
          <RichText
            answering={answering}
            className={className}
            disabled={disabled}
            error={!!error}
            initialValue={initialValue}
            placeholder={placeholder}
            hasToolbar
            onBlur={onBlur}
            onChange={onChangeHandler}
            onFocus={onFocus}
            onSend={onSend}
          />
          <Toolbar
            gaEventCategory={gaEventCategory}
            isSendEnabled={isSendEnabled}
            selectedTools={toolbarItems}
            withCancel={!!onCancel}
            withSend={!!onSend}
            onCancel={onCancel}
            onSend={onSend}
          />
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </div>
      </>
    );
  }
);

TextEditor.displayName = 'TextEditor';

TextEditor.propTypes = {
  /**
   * Who user is answering in text-editor
   */
  answering: PropTypes.string,
  /**
   * Whether the text-editor field is disabled
   */
  disabled: PropTypes.bool,
  /**
   * Whether the text-editor should render a error state
   */
  error: PropTypes.string,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Initial value for uncontrolled component
   */
  initialValue: PropTypes.string,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * GA Events
   */
  gaEventCategory: PropTypes.string,
  /**
   * Text to be rendered as a label
   */
  label: PropTypes.string,
  /**
   * Text that appears in the form control when the it is empty
   */
  placeholder: PropTypes.string.isRequired,
  /**
   * Function to set the visibility of comment tooltip
   */
  toolbarItems: PropTypes.arrayOf(PropTypes.string),
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Callback function on blur
   */
  onBlur: PropTypes.func,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Callback function on cancel
   */
  onCancel: PropTypes.func,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Callback function on change
   */
  onChange: PropTypes.func,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Callback function on focus
   */
  onFocus: PropTypes.func,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Callback function on send
   */
  onSend: PropTypes.func,
};

export default TextEditor;
